// ===================================================================
// Time & Labor — Time Entry (Multi-date Timecard)
// Timecard-style form: select employee, enter time for multiple dates
// ===================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import { usePolling } from '../../hooks/usePolling';
import {
  Title, Text, Paper, Table, Button, Group, TextInput, Select, Modal,
  SimpleGrid, Badge, ActionIcon, LoadingOverlay,
  Checkbox, NumberInput, Divider, Stack, Tooltip,
} from '@mantine/core';
import {
  IconPlus, IconTrash, IconSearch, IconCalendar,
  IconDeviceFloppy, IconCalendarPlus, IconEdit,
  IconAlertTriangle, IconSend,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useTimeLaborStore } from '../../store/timeLaborStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { formatCurrency, stripCurrency } from '../../lib/formatUtils';
import { fetchIgnatiusUsers, WORKER_URL, type IgnatiusUser } from '../../lib/api-switch';
import type { PALaborRecord } from '../../types/pagrants';
import { checkEligibility, type EligibilityFlag } from '../../lib/eligibility';

const TEAL = '#0D9488';

const STATUS_COLORS: Record<string, string> = {
  draft: 'gray', submitted: 'blue', approved: 'green', rejected: 'red',
};

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] || 'gray';
  return <Badge size="sm" variant="light" color={color}>{status || 'draft'}</Badge>;
}

const TASK_TYPES = [
  { value: 'debris_removal', label: 'Debris Removal', category: 'A' },
  { value: 'traffic_control', label: 'Traffic Control', category: 'B' },
  { value: 'search_rescue', label: 'Search & Rescue', category: 'B' },
  { value: 'eoc_operations', label: 'EOC Operations', category: 'B' },
  { value: 'shelter_management', label: 'Shelter Management', category: 'B' },
  { value: 'emergency_protective', label: 'Emergency Protective Measures', category: 'B' },
  { value: 'security_patrol', label: 'Security / Law Enforcement', category: 'B' },
  { value: 'road_bridge_repair', label: 'Road & Bridge Repair', category: 'C' },
  { value: 'water_sewer_repair', label: 'Water / Sewer Repair', category: 'D' },
  { value: 'building_repair', label: 'Building Repair', category: 'E' },
  { value: 'utility_restoration', label: 'Utility Restoration', category: 'F' },
  { value: 'park_recreation', label: 'Park & Recreation Repair', category: 'G' },
  { value: 'admin_management', label: 'Administrative / Management', category: 'Z' },
  { value: 'other', label: 'Other', category: '' },
];

const TASK_OPTIONS = TASK_TYPES.map(t => ({ value: t.value, label: t.label }));

function getTaskLabel(value: string): string {
  return TASK_TYPES.find(t => t.value === value)?.label || value;
}

function getTaskCategory(value: string): string {
  return TASK_TYPES.find(t => t.value === value)?.category || '';
}

function pf(v: string | undefined): number {
  return parseFloat(stripCurrency(v || '')) || 0;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Get the Monday of the week containing the given date */
function getMondayOfWeek(dateStr: string): Date {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function formatDateISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

interface TimecardRow {
  id: number; // local key
  date: string;
  regHours: number;
  otHours: number;
  notes: string;
  site: string;
  task: string;
  shiftPremium: string;
  accountCode: string;
  programCode: string;
}

interface CodeEntry { code: string; description: string; }
interface ShiftPremiumEntry { type: string; label: string; multiplier: number; }

const DEFAULT_PREMIUMS: ShiftPremiumEntry[] = [
  { type: 'night', label: 'Night Differential', multiplier: 1.1 },
  { type: 'weekend', label: 'Weekend', multiplier: 1.5 },
  { type: 'holiday', label: 'Holiday', multiplier: 2.0 },
  { type: 'hazard', label: 'Hazard Pay', multiplier: 1.25 },
];

const PREMIUM_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'night', label: 'Night Differential' },
  { value: 'weekend', label: 'Weekend' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'hazard', label: 'Hazard Pay' },
];

let rowIdCounter = 1;
function newRow(date?: string): TimecardRow {
  return { id: rowIdCounter++, date: date || todayStr(), regHours: 0, otHours: 0, notes: '', site: '', task: '', shiftPremium: 'none', accountCode: '', programCode: '' };
}

export default function TimeEntry() {
  const { canEdit } = useModulePermission('timelabor');
  const activeIncident = useAuthStore(s => s.activeIncident);

  const {
    employees, fetchEmployees,
    laborRecords, loadingRecords, fetchLaborRecords,
    createLaborRecord, updateLaborRecord, deleteLaborRecord,
  } = useTimeLaborStore();
  const { projects, fetchProjects } = usePAGrantsStore();

  const [users, setUsers] = useState<IgnatiusUser[]>([]);

  // Timecard form state
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [sameJobSite, setSameJobSite] = useState(true);
  const [globalSite, setGlobalSite] = useState('');
  const [sameActivity, setSameActivity] = useState(true);
  const [globalTask, setGlobalTask] = useState<string | null>(null);
  const [rows, setRows] = useState<TimecardRow[]>([newRow()]);
  const [submitting, setSubmitting] = useState(false);

  // Codes & premiums
  const [accountCodes, setAccountCodes] = useState<CodeEntry[]>([]);
  const [programCodes, setProgramCodes] = useState<CodeEntry[]>([]);
  const [shiftPremiums, setShiftPremiums] = useState<ShiftPremiumEntry[]>(DEFAULT_PREMIUMS);
  const [sameCodes, setSameCodes] = useState(true);
  const [globalAccountCode, setGlobalAccountCode] = useState('');
  const [globalProgramCode, setGlobalProgramCode] = useState('');

  // Week add
  const [weekStart, setWeekStart] = useState(formatDateISO(getMondayOfWeek(todayStr())));

  // Recent entries filters
  const [search, setSearch] = useState('');
  const [filterEmployee, setFilterEmployee] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Detail view
  const [detailRecord, setDetailRecord] = useState<PALaborRecord | null>(null);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<PALaborRecord | null>(null);

  // Bulk submit for review
  const [bulkSelected, setBulkSelected] = useState<Set<number>>(new Set());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Edit existing entry
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PALaborRecord | null>(null);
  const [editForm, setEditForm] = useState({ regHours: '', otHours: '', notes: '', category: '', activityDescription: '', accountCode: '', programCode: '', shiftPremium: 'none' });
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchLaborRecords();
    fetchProjects();
    loadUsers();
    loadCodeConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  usePolling(() => { fetchLaborRecords(); });

  const loadCodeConfig = async () => {
    const kvH = { Origin: 'https://apps.opengov.com' };
    try {
      const [acRes, pcRes, spRes] = await Promise.all([
        fetch(`${WORKER_URL}/api/config/timelabor-account-codes`, { headers: kvH }),
        fetch(`${WORKER_URL}/api/config/timelabor-program-codes`, { headers: kvH }),
        fetch(`${WORKER_URL}/api/config/timelabor-shift-premiums`, { headers: kvH }),
      ]);
      if (acRes.ok) { const d = await acRes.json(); if (Array.isArray(d)) setAccountCodes(d); }
      if (pcRes.ok) { const d = await pcRes.json(); if (Array.isArray(d)) setProgramCodes(d); }
      if (spRes.ok) { const d = await spRes.json(); if (Array.isArray(d) && d.length > 0) setShiftPremiums(d.filter((p: ShiftPremiumEntry) => p.type !== 'none')); }
    } catch { /* ignore */ }
  };

  const loadUsers = async () => {
    try {
      const list = await fetchIgnatiusUsers();
      setUsers(list);
    } catch (e) {
      console.error('Failed to fetch users', e);
    }
  };

  // Build email -> labor profile map
  const profileByEmail = useMemo(() => {
    const m = new Map<string, typeof employees[0]>();
    employees.forEach(e => {
      if (e.rosterEmail) m.set(e.rosterEmail.toLowerCase(), e);
    });
    return m;
  }, [employees]);

  // Build name -> labor profile map (for eligibility checks on recent entries)
  const profileByName = useMemo(() => {
    const m = new Map<string, typeof employees[0]>();
    employees.forEach(e => {
      if (e.employeeName) m.set(e.employeeName, e);
    });
    return m;
  }, [employees]);

  // Users with labor profiles for employee dropdown
  const employeeOptions = useMemo(() => {
    return users
      .filter(u => profileByEmail.has(u.email.toLowerCase()))
      .map(u => ({
        value: u.email,
        label: `${u.fullName} (${u.email})`,
      }));
  }, [users, profileByEmail]);

  const selectedProfile = useMemo(() => {
    if (!selectedEmail) return null;
    return profileByEmail.get(selectedEmail.toLowerCase()) || null;
  }, [selectedEmail, profileByEmail]);

  const selectedUser = useMemo(() => {
    if (!selectedEmail) return null;
    return users.find(u => u.email.toLowerCase() === selectedEmail.toLowerCase()) || null;
  }, [selectedEmail, users]);

  // Auto-fill default codes when employee changes
  useEffect(() => {
    if (selectedProfile) {
      if (selectedProfile.defaultAccountCode) setGlobalAccountCode(selectedProfile.defaultAccountCode);
      if (selectedProfile.defaultProgramCode) setGlobalProgramCode(selectedProfile.defaultProgramCode);
    }
  }, [selectedProfile]);

  // Project lookup for recent entries display
  const projectLookup = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach(p => m.set(String(p.Rid), p.projectTitle || p.projectNumber || `#${p.Rid}`));
    return m;
  }, [projects]);

  // ── Row management ──────────────────────────────────────────────

  const updateRow = useCallback((id: number, patch: Partial<TimecardRow>) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  }, []);

  const removeRow = useCallback((id: number) => {
    setRows(prev => prev.length <= 1 ? prev : prev.filter(r => r.id !== id));
  }, []);

  const addRow = useCallback(() => {
    setRows(prev => [...prev, newRow()]);
  }, []);

  const addWeek = useCallback(() => {
    const monday = getMondayOfWeek(weekStart);
    const newRows: TimecardRow[] = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      newRows.push(newRow(formatDateISO(d)));
    }
    setRows(prev => [...prev, ...newRows]);
  }, [weekStart]);

  // ── Premium helper ──────────────────────────────────────────────

  const getPremiumMultiplier = useCallback((premType: string): number => {
    if (!premType || premType === 'none') return 1.0;
    const found = shiftPremiums.find(p => p.type === premType);
    return found ? found.multiplier : 1.0;
  }, [shiftPremiums]);

  const getPremiumLabel = useCallback((premType: string): string => {
    if (!premType || premType === 'none') return '';
    const found = shiftPremiums.find(p => p.type === premType);
    return found ? found.label : premType;
  }, [shiftPremiums]);

  // ── Cost calculations ───────────────────────────────────────────

  const totals = useMemo(() => {
    const regRate = pf(selectedProfile?.regularRate);
    const otRate = pf(selectedProfile?.overtimeRate);
    const fringePct = pf(selectedProfile?.totalFringePct);

    let totalReg = 0, totalOT = 0, laborCost = 0;
    rows.forEach(r => {
      totalReg += r.regHours;
      totalOT += r.otHours;
      const mult = getPremiumMultiplier(r.shiftPremium);
      laborCost += (r.regHours * regRate * mult) + (r.otHours * otRate * mult);
    });
    const fringeCost = laborCost * fringePct / 100;
    return { totalReg, totalOT, laborCost, fringeCost, grandTotal: laborCost + fringeCost };
  }, [rows, selectedProfile, getPremiumMultiplier]);

  // ── Submit timecard ─────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!selectedProfile || !selectedUser) {
      notifications.show({ title: 'Required', message: 'Select an employee with a labor profile', color: 'red' });
      return;
    }

    const validRows = rows.filter(r => r.date && (r.regHours > 0 || r.otHours > 0));
    if (validRows.length === 0) {
      notifications.show({ title: 'Required', message: 'Enter at least one row with hours', color: 'red' });
      return;
    }

    // ── Eligibility checks (warn but don't block) ──────────────
    const allFlags: EligibilityFlag[] = [];
    for (const row of validRows) {
      const taskValue = sameActivity ? (globalTask || '') : row.task;
      const taskCategory = taskValue ? getTaskCategory(taskValue) : '';
      const flags = checkEligibility(
        selectedProfile.budgetStatus,
        selectedProfile.flsaStatus,
        selectedProfile.jobTitle,
        taskCategory,
        row.regHours,
        row.otHours,
      );
      allFlags.push(...flags);
    }
    // Deduplicate by code
    const seenCodes = new Set<string>();
    for (const flag of allFlags) {
      if (seenCodes.has(flag.code)) continue;
      seenCodes.add(flag.code);
      notifications.show({
        title: flag.type === 'warning' ? 'Eligibility Warning' : 'Eligibility Note',
        message: flag.message,
        color: flag.type === 'warning' ? 'orange' : 'blue',
        autoClose: 10000,
      });
    }

    setSubmitting(true);

    const regRate = pf(selectedProfile.regularRate);
    const otRate = pf(selectedProfile.overtimeRate);
    const fringePct = pf(selectedProfile.totalFringePct);
    const ficaPct = pf(selectedProfile.fringeFicaPct);
    const retPct = pf(selectedProfile.fringeRetirementPct);
    const healthPct = pf(selectedProfile.fringeHealthPct);
    const wcPct = pf(selectedProfile.fringeWorkersCompPct);
    const uiPct = pf(selectedProfile.fringeUnemploymentPct);
    const otherPct = pf(selectedProfile.fringeOtherPct);

    let created = 0;
    for (const row of validRows) {
      const taskValue = sameActivity ? (globalTask || '') : row.task;
      const taskLabel = taskValue ? getTaskLabel(taskValue) : '';
      const taskCategory = taskValue ? getTaskCategory(taskValue) : '';
      const site = sameJobSite ? globalSite : row.site;

      const premMult = getPremiumMultiplier(row.shiftPremium);
      const effRegRate = regRate * premMult;
      const effOtRate = otRate * premMult;
      const laborCost = (row.regHours * effRegRate) + (row.otHours * effOtRate);
      const fringeCost = laborCost * fringePct / 100;

      const rowAccountCode = sameCodes ? globalAccountCode : (row.accountCode || globalAccountCode);
      const rowProgramCode = sameCodes ? globalProgramCode : (row.programCode || globalProgramCode);

      const actDesc = row.notes
        ? (taskLabel ? `${taskLabel}: ${row.notes}` : row.notes)
        : taskLabel;

      await createLaborRecord({
        projectId: '',
        organizationId: selectedProfile.organizationId || '',
        employeeName: selectedUser.fullName || selectedProfile.employeeName || '',
        employeeTitle: selectedProfile.jobTitle || '',
        employeeId: selectedProfile.employeeId || '',
        workDate: row.date,
        hoursRegular: row.regHours.toString(),
        hoursOvertime: row.otHours.toString(),
        rateRegular: selectedProfile.regularRate,
        rateOvertime: selectedProfile.overtimeRate,
        fringeBenefitsPct: selectedProfile.totalFringePct,
        fringeFica: (laborCost * ficaPct / 100).toFixed(2),
        fringeRetirement: (laborCost * retPct / 100).toFixed(2),
        fringeHealth: (laborCost * healthPct / 100).toFixed(2),
        fringeWorkersComp: (laborCost * wcPct / 100).toFixed(2),
        fringeUnemployment: (laborCost * uiPct / 100).toFixed(2),
        fringeOther: (laborCost * otherPct / 100).toFixed(2),
        laborCost: laborCost.toFixed(2),
        fringeCost: fringeCost.toFixed(2),
        totalCost: (laborCost + fringeCost).toFixed(2),
        activityDescription: actDesc,
        category: taskCategory,
        timesheetOnFile: 'draft',
        notes: site || '',
        incidentId: activeIncident?.id ? String(activeIncident.id) : '',
        accountCode: rowAccountCode,
        programCode: rowProgramCode,
        shiftPremium: row.shiftPremium || 'none',
      });
      created++;
    }

    notifications.show({
      title: 'Timecard Submitted',
      message: `${created} time entr${created === 1 ? 'y' : 'ies'} created for ${selectedUser.fullName}`,
      color: TEAL,
    });

    // Reset form rows but keep employee selected
    setRows([newRow()]);
    setGlobalSite('');
    setGlobalTask(null);
    setSubmitting(false);
  };

  // ── Recent entries filtering ────────────────────────────────────

  const employeeFilterOptions = useMemo(() => {
    const names = new Set(laborRecords.map(r => r.employeeName).filter(Boolean));
    return [...names].sort().map(n => ({ value: n, label: n }));
  }, [laborRecords]);

  const filteredRecords = useMemo(() => {
    let list = laborRecords;
    // Filter by active incident
    if (activeIncident) list = list.filter(r => !r.incidentId || r.incidentId === String(activeIncident.id));
    if (filterEmployee) list = list.filter(r => r.employeeName === filterEmployee);
    if (dateFrom) list = list.filter(r => r.workDate >= dateFrom);
    if (dateTo) list = list.filter(r => r.workDate <= dateTo);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.employeeName.toLowerCase().includes(q) ||
        r.activityDescription.toLowerCase().includes(q) ||
        r.notes.toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => b.workDate.localeCompare(a.workDate) || a.employeeName.localeCompare(b.employeeName));
  }, [laborRecords, search, filterEmployee, dateFrom, dateTo]);

  // ── Eligibility flags for recent entries ────────────────────────
  const entryFlags = useMemo(() => {
    const map = new Map<number, EligibilityFlag[]>();
    for (const rec of filteredRecords) {
      const profile = profileByName.get(rec.employeeName);
      if (!profile) continue;
      const cat = rec.category || '';
      const flags = checkEligibility(
        profile.budgetStatus,
        profile.flsaStatus,
        profile.jobTitle || rec.employeeTitle,
        cat,
        pf(rec.hoursRegular),
        pf(rec.hoursOvertime),
      );
      if (flags.length > 0) map.set(rec.Rid, flags);
    }
    return map;
  }, [filteredRecords, profileByName]);

  const flaggedCount = entryFlags.size;

  // ── Delete ──────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteLaborRecord(deleteTarget.Rid);
    notifications.show({ title: 'Deleted', message: 'Time entry removed', color: 'red' });
    setDeleteTarget(null);
  };

  // ── Edit existing ───────────────────────────────────────────────

  const openEdit = (rec: PALaborRecord) => {
    setEditingRecord(rec);
    setEditForm({
      regHours: rec.hoursRegular,
      otHours: rec.hoursOvertime,
      notes: rec.notes,
      category: rec.category,
      activityDescription: rec.activityDescription,
      accountCode: rec.accountCode || '',
      programCode: rec.programCode || '',
      shiftPremium: rec.shiftPremium || 'none',
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingRecord) return;
    setEditSaving(true);
    const regHrs = pf(editForm.regHours);
    const otHrs = pf(editForm.otHours);
    const regRate = pf(editingRecord.rateRegular);
    const otRate = pf(editingRecord.rateOvertime);
    const fringePct = pf(editingRecord.fringeBenefitsPct);
    const premMult = getPremiumMultiplier(editForm.shiftPremium);
    const laborCost = (regHrs * regRate * premMult) + (otHrs * otRate * premMult);
    const fringeCost = laborCost * fringePct / 100;

    await updateLaborRecord(editingRecord.Rid, {
      hoursRegular: regHrs.toString(),
      hoursOvertime: otHrs.toString(),
      laborCost: laborCost.toFixed(2),
      fringeCost: fringeCost.toFixed(2),
      totalCost: (laborCost + fringeCost).toFixed(2),
      activityDescription: editForm.activityDescription,
      category: editForm.category,
      notes: editForm.notes,
      accountCode: editForm.accountCode,
      programCode: editForm.programCode,
      shiftPremium: editForm.shiftPremium,
    });
    notifications.show({ title: 'Updated', message: 'Time entry updated', color: TEAL });
    setEditSaving(false);
    setEditModalOpen(false);
  };

  // ── Submit for review ────────────────────────────────────────────

  const handleSubmitForReview = async (rid: number) => {
    await updateLaborRecord(rid, { timesheetOnFile: 'submitted' });
    notifications.show({ title: 'Submitted', message: 'Time entry submitted for supervisor review', color: 'blue' });
    setDetailRecord(null);
  };

  const handleResubmit = async (rid: number) => {
    const rec = laborRecords.find(r => r.Rid === rid);
    const notes = rec?.notes || '';
    // Strip existing rejection prefix
    const cleanNotes = notes.replace(/^\[REJECTED:.*?\]\s*/, '');
    await updateLaborRecord(rid, { timesheetOnFile: 'submitted', notes: cleanNotes });
    notifications.show({ title: 'Resubmitted', message: 'Time entry resubmitted for review', color: 'blue' });
    setDetailRecord(null);
  };

  const handleBulkSubmitForReview = async () => {
    if (bulkSelected.size === 0) return;
    setBulkSubmitting(true);
    let count = 0;
    for (const rid of bulkSelected) {
      const rec = laborRecords.find(r => r.Rid === rid);
      if (rec && (!rec.timesheetOnFile || rec.timesheetOnFile === 'draft' || rec.timesheetOnFile === 'no')) {
        await updateLaborRecord(rid, { timesheetOnFile: 'submitted' });
        count++;
      }
    }
    notifications.show({ title: 'Submitted', message: `${count} entries submitted for supervisor review`, color: 'blue' });
    setBulkSelected(new Set());
    setBulkSubmitting(false);
  };

  // Draft entries available for bulk submit
  const draftRecords = useMemo(
    () => filteredRecords.filter(r => !r.timesheetOnFile || r.timesheetOnFile === 'draft' || r.timesheetOnFile === 'no'),
    [filteredRecords]
  );

  // ── Summary stats for recent entries ────────────────────────────

  const recentTotalHours = useMemo(() =>
    filteredRecords.reduce((s, r) => s + pf(r.hoursRegular) + pf(r.hoursOvertime), 0),
    [filteredRecords]
  );
  const recentTotalCost = useMemo(() =>
    filteredRecords.reduce((s, r) => s + pf(r.totalCost), 0),
    [filteredRecords]
  );

  // ── Helpers for task display ────────────────────────────────────

  /** Extract task label from activityDescription: may be "TaskLabel: notes" or just "TaskLabel" */
  function parseTaskFromActivity(desc: string): { taskLabel: string; category: string } {
    for (const t of TASK_TYPES) {
      if (desc === t.label || desc.startsWith(t.label + ':') || desc.startsWith(t.label + ': ')) {
        return { taskLabel: t.label, category: t.category };
      }
    }
    return { taskLabel: desc, category: '' };
  }

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={loadingRecords} />

      <Group justify="space-between" mb="md">
        <div>
          <Title order={3}>Time Entry</Title>
          <Text size="sm" c="dimmed">Fill out a timecard — enter time for multiple dates at once</Text>
        </div>
      </Group>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* TIMECARD FORM                                               */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {canEdit && (
        <Paper p="md" withBorder mb="xl" style={{ borderTop: `3px solid ${TEAL}` }}>
          {/* Employee selector */}
          <Select
            label="Employee"
            placeholder="Select employee with labor profile..."
            data={employeeOptions}
            value={selectedEmail}
            onChange={setSelectedEmail}
            searchable
            size="sm"
            mb="sm"
            style={{ maxWidth: 400 }}
          />

          {/* Employee info bar */}
          {selectedProfile && (
            <Paper p="xs" withBorder bg="gray.0" mb="md">
              <Group gap={16}>
                <div><Text size="xs" c="dimmed">Title</Text><Text size="sm" fw={500}>{selectedProfile.jobTitle || '-'}</Text></div>
                <div><Text size="xs" c="dimmed">Reg Rate</Text><Text size="sm" fw={600}>{formatCurrency(selectedProfile.regularRate)}/hr</Text></div>
                <div><Text size="xs" c="dimmed">OT Rate</Text><Text size="sm" fw={600}>{formatCurrency(selectedProfile.overtimeRate)}/hr</Text></div>
                <div><Text size="xs" c="dimmed">Fringe</Text><Text size="sm" fw={600}>{pf(selectedProfile.totalFringePct).toFixed(1)}%</Text></div>
              </Group>
            </Paper>
          )}

          {/* Toggle controls */}
          <Group gap={24} mb="sm">
            <Checkbox
              label="Same job site for all entries"
              checked={sameJobSite}
              onChange={e => setSameJobSite(e.currentTarget.checked)}
              size="xs"
            />
            <Checkbox
              label="Same activity for all entries"
              checked={sameActivity}
              onChange={e => setSameActivity(e.currentTarget.checked)}
              size="xs"
            />
            {(accountCodes.length > 0 || programCodes.length > 0) && (
              <Checkbox
                label="Same codes for all entries"
                checked={sameCodes}
                onChange={e => setSameCodes(e.currentTarget.checked)}
                size="xs"
              />
            )}
          </Group>

          {/* Global site / task / codes when toggled */}
          <Group gap={8} mb="sm">
            {sameJobSite && (
              <TextInput
                label="Job Site"
                placeholder="Location / site name"
                value={globalSite}
                onChange={e => setGlobalSite(e.currentTarget.value)}
                size="xs"
                style={{ width: 250 }}
              />
            )}
            {sameActivity && (
              <Select
                label="Activity"
                placeholder="Select task..."
                data={TASK_OPTIONS}
                value={globalTask}
                onChange={setGlobalTask}
                clearable
                size="xs"
                style={{ width: 260 }}
              />
            )}
            {sameCodes && accountCodes.length > 0 && (
              <Select
                label="Account Code"
                placeholder="Select..."
                data={accountCodes.map(c => ({ value: c.code, label: `${c.code} — ${c.description}` }))}
                value={globalAccountCode || null}
                onChange={v => setGlobalAccountCode(v || '')}
                clearable searchable
                size="xs"
                style={{ width: 260 }}
              />
            )}
            {sameCodes && programCodes.length > 0 && (
              <Select
                label="Program Code"
                placeholder="Select..."
                data={programCodes.map(c => ({ value: c.code, label: `${c.code} — ${c.description}` }))}
                value={globalProgramCode || null}
                onChange={v => setGlobalProgramCode(v || '')}
                clearable searchable
                size="xs"
                style={{ width: 260 }}
              />
            )}
          </Group>

          <Divider mb="sm" />

          {/* Entry rows */}
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 145 }}>Date</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'right' }}>Reg Hours</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'right' }}>OT Hours</Table.Th>
                {!sameJobSite && <Table.Th style={{ width: 160 }}>Site</Table.Th>}
                {!sameActivity && <Table.Th style={{ width: 200 }}>Task</Table.Th>}
                {!sameCodes && accountCodes.length > 0 && <Table.Th style={{ width: 160 }}>Account</Table.Th>}
                {!sameCodes && programCodes.length > 0 && <Table.Th style={{ width: 160 }}>Program</Table.Th>}
                <Table.Th style={{ width: 150 }}>Shift Premium</Table.Th>
                <Table.Th>Notes</Table.Th>
                <Table.Th style={{ width: 36 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(row => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <TextInput type="date" size="xs" value={row.date}
                      onChange={e => updateRow(row.id, { date: e.currentTarget.value })} />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput size="xs" min={0} max={24} decimalScale={1} value={row.regHours}
                      onChange={v => updateRow(row.id, { regHours: Number(v) || 0 })}
                      styles={{ input: { textAlign: 'right' } }} />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput size="xs" min={0} max={24} decimalScale={1} value={row.otHours}
                      onChange={v => updateRow(row.id, { otHours: Number(v) || 0 })}
                      styles={{ input: { textAlign: 'right' } }} />
                  </Table.Td>
                  {!sameJobSite && (
                    <Table.Td>
                      <TextInput size="xs" placeholder="Site" value={row.site}
                        onChange={e => updateRow(row.id, { site: e.currentTarget.value })} />
                    </Table.Td>
                  )}
                  {!sameActivity && (
                    <Table.Td>
                      <Select size="xs" placeholder="Task" data={TASK_OPTIONS} value={row.task || null}
                        onChange={v => updateRow(row.id, { task: v || '' })} clearable />
                    </Table.Td>
                  )}
                  {!sameCodes && accountCodes.length > 0 && (
                    <Table.Td>
                      <Select size="xs" placeholder="Account" value={row.accountCode || null}
                        data={accountCodes.map(c => ({ value: c.code, label: c.code }))}
                        onChange={v => updateRow(row.id, { accountCode: v || '' })} clearable />
                    </Table.Td>
                  )}
                  {!sameCodes && programCodes.length > 0 && (
                    <Table.Td>
                      <Select size="xs" placeholder="Program" value={row.programCode || null}
                        data={programCodes.map(c => ({ value: c.code, label: c.code }))}
                        onChange={v => updateRow(row.id, { programCode: v || '' })} clearable />
                    </Table.Td>
                  )}
                  <Table.Td>
                    <Select size="xs" data={PREMIUM_OPTIONS} value={row.shiftPremium || 'none'}
                      onChange={v => updateRow(row.id, { shiftPremium: v || 'none' })} />
                  </Table.Td>
                  <Table.Td>
                    <TextInput size="xs" placeholder="Optional notes" value={row.notes}
                      onChange={e => updateRow(row.id, { notes: e.currentTarget.value })} />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon size="xs" variant="subtle" color="red" onClick={() => removeRow(row.id)}
                      disabled={rows.length <= 1}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {/* Add row / week buttons */}
          <Group gap={8} mt="sm">
            <Button variant="light" color={TEAL} size="xs" leftSection={<IconPlus size={14} />} onClick={addRow}>
              Add Date
            </Button>
            <Divider orientation="vertical" />
            <TextInput type="date" size="xs" value={weekStart}
              onChange={e => setWeekStart(e.currentTarget.value)}
              style={{ width: 145 }}
              leftSection={<IconCalendar size={14} />} />
            <Button variant="light" color="blue" size="xs" leftSection={<IconCalendarPlus size={14} />} onClick={addWeek}>
              Add Week (Mon-Fri)
            </Button>
          </Group>

          <Divider my="sm" />

          {/* Running totals */}
          <Paper p="xs" withBorder bg="gray.0" mb="sm">
            <SimpleGrid cols={{ base: 2, sm: 5 }}>
              <div style={{ textAlign: 'center' }}>
                <Text size="xs" c="dimmed">Reg Hours</Text>
                <Text fw={600}>{totals.totalReg.toFixed(1)}</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text size="xs" c="dimmed">OT Hours</Text>
                <Text fw={600}>{totals.totalOT.toFixed(1)}</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text size="xs" c="dimmed">Labor Cost</Text>
                <Text fw={600}>{formatCurrency(totals.laborCost.toFixed(2))}</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text size="xs" c="dimmed">Fringe</Text>
                <Text fw={600}>{formatCurrency(totals.fringeCost.toFixed(2))}</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text size="xs" c="dimmed">Grand Total</Text>
                <Text fw={700} c={TEAL}>{formatCurrency(totals.grandTotal.toFixed(2))}</Text>
              </div>
            </SimpleGrid>
          </Paper>

          <Group justify="flex-end">
            <Button color={TEAL} leftSection={<IconDeviceFloppy size={14} />}
              onClick={handleSubmit} loading={submitting}
              disabled={!selectedProfile}>
              Submit Timecard
            </Button>
          </Group>
        </Paper>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* RECENT ENTRIES                                              */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <Divider mb="md" />
      <Group justify="space-between" mb="md">
        <Title order={4}>Recent Entries</Title>
      </Group>

      {/* Filters */}
      <Group gap={8} mb="md">
        <TextInput size="xs" placeholder="Search..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.currentTarget.value)} style={{ maxWidth: 200 }} />
        <TextInput size="xs" type="date" value={dateFrom}
          onChange={e => setDateFrom(e.currentTarget.value)} style={{ width: 140 }}
          leftSection={<IconCalendar size={14} />} />
        <TextInput size="xs" type="date" value={dateTo}
          onChange={e => setDateTo(e.currentTarget.value)} style={{ width: 140 }} />
        <Select size="xs" placeholder="Employee" data={employeeFilterOptions} value={filterEmployee}
          onChange={setFilterEmployee} clearable searchable style={{ width: 180 }} />
      </Group>

      {/* Summary bar */}
      <Group gap={12} mb="md">
        <Badge size="lg" variant="light" color="blue">{filteredRecords.length} entries</Badge>
        <Badge size="lg" variant="light" color={TEAL}>{recentTotalHours.toFixed(1)} hrs</Badge>
        <Badge size="lg" variant="light" color="green">{formatCurrency(recentTotalCost.toFixed(2))}</Badge>
        {flaggedCount > 0 && (
          <Tooltip label="Entries with PAPPG eligibility warnings">
            <Badge size="lg" variant="light" color="yellow" leftSection={<IconAlertTriangle size={12} />}>
              {flaggedCount} flagged
            </Badge>
          </Tooltip>
        )}
      </Group>

      {/* Bulk submit bar */}
      {canEdit && bulkSelected.size > 0 && (
        <Paper p="xs" withBorder mb="md" bg="blue.0">
          <Group justify="space-between">
            <Text size="sm" fw={500}>{bulkSelected.size} draft entries selected</Text>
            <Button size="xs" color="blue" leftSection={<IconSend size={14} />}
              onClick={handleBulkSubmitForReview} loading={bulkSubmitting}>
              Submit Selected for Review
            </Button>
          </Group>
        </Paper>
      )}

      {/* Entries table */}
      <Paper withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              {canEdit && (
                <Table.Th w={40}>
                  <Checkbox
                    size="xs"
                    checked={draftRecords.length > 0 && draftRecords.every(r => bulkSelected.has(r.Rid))}
                    indeterminate={draftRecords.some(r => bulkSelected.has(r.Rid)) && !draftRecords.every(r => bulkSelected.has(r.Rid))}
                    onChange={() => {
                      const allSelected = draftRecords.every(r => bulkSelected.has(r.Rid));
                      if (allSelected) setBulkSelected(new Set());
                      else setBulkSelected(new Set(draftRecords.map(r => r.Rid)));
                    }}
                  />
                </Table.Th>
              )}
              <Table.Th>Date</Table.Th>
              <Table.Th>Employee</Table.Th>
              <Table.Th>Activity</Table.Th>
              <Table.Th>Project</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Reg</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>OT</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Total $</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Premium</Table.Th>
              <Table.Th>Site</Table.Th>
              {canEdit && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredRecords.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={12}>
                  <Text size="sm" c="dimmed" ta="center" py="lg">No time entries found.</Text>
                </Table.Td>
              </Table.Tr>
            ) : filteredRecords.map(r => {
              const parsed = parseTaskFromActivity(r.activityDescription);
              const recFlags = entryFlags.get(r.Rid);
              const status = r.timesheetOnFile || 'draft';
              const isDraft = !r.timesheetOnFile || r.timesheetOnFile === 'draft' || r.timesheetOnFile === 'no';
              return (
                <Table.Tr key={r.Rid} style={{ cursor: 'pointer' }} onClick={() => setDetailRecord(r)}>
                  {canEdit && (
                    <Table.Td onClick={e => e.stopPropagation()}>
                      {isDraft && (
                        <Checkbox size="xs" checked={bulkSelected.has(r.Rid)}
                          onChange={() => {
                            const next = new Set(bulkSelected);
                            if (next.has(r.Rid)) next.delete(r.Rid);
                            else next.add(r.Rid);
                            setBulkSelected(next);
                          }} />
                      )}
                    </Table.Td>
                  )}
                  <Table.Td>{r.workDate}</Table.Td>
                  <Table.Td fw={500}>
                    <Group gap={4} wrap="nowrap">
                      {r.employeeName}
                      {recFlags && recFlags.length > 0 && (
                        <Tooltip label={recFlags.map(f => f.message).join(' | ')} multiline w={340}>
                          <IconAlertTriangle size={14} color="#F59E0B" style={{ flexShrink: 0 }} />
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ maxWidth: 200 }}>
                    <Group gap={4} wrap="nowrap">
                      <Text size="sm" truncate>{parsed.taskLabel}</Text>
                      {(r.category || parsed.category) && (
                        <Badge size="xs" variant="light" color="gray">
                          {r.category || parsed.category}
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Text size="sm" c={r.projectId ? undefined : 'dimmed'}>
                      {r.projectId ? (projectLookup.get(r.projectId) || r.projectId) : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{pf(r.hoursRegular).toFixed(1)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{pf(r.hoursOvertime) > 0 ? pf(r.hoursOvertime).toFixed(1) : '-'}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }} fw={600}>{formatCurrency(r.totalCost)}</Table.Td>
                  <Table.Td><StatusBadge status={status === 'no' ? 'draft' : status} /></Table.Td>
                  <Table.Td>
                    {r.shiftPremium && r.shiftPremium !== 'none' && (
                      <Badge size="xs" variant="light" color="violet">
                        {getPremiumLabel(r.shiftPremium) || r.shiftPremium}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Text size="sm" c="dimmed">{r.notes?.replace(/^\[REJECTED:.*?\]\s*/, '') || '-'}</Text>
                  </Table.Td>
                  {canEdit && (
                    <Table.Td>
                      <Group gap={4}>
                        <ActionIcon size="xs" variant="subtle" color="blue" onClick={(e) => { e.stopPropagation(); openEdit(r); }}>
                          <IconEdit size={14} />
                        </ActionIcon>
                        <ActionIcon size="xs" variant="subtle" color="red" onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Edit Modal */}
      <Modal opened={editModalOpen} onClose={() => setEditModalOpen(false)} title="Edit Time Entry" size="md">
        {editingRecord && (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              {editingRecord.employeeName} -- {editingRecord.workDate}
            </Text>
            <SimpleGrid cols={2}>
              <TextInput label="Regular Hours" value={editForm.regHours}
                onChange={e => setEditForm(prev => ({ ...prev, regHours: e.currentTarget.value }))} size="sm" />
              <TextInput label="Overtime Hours" value={editForm.otHours}
                onChange={e => setEditForm(prev => ({ ...prev, otHours: e.currentTarget.value }))} size="sm" />
            </SimpleGrid>
            <Select label="Task" data={TASK_OPTIONS} value={
              TASK_TYPES.find(t => t.label === editForm.activityDescription || editForm.activityDescription.startsWith(t.label + ':'))?.value || null
            } onChange={v => {
              const t = TASK_TYPES.find(tt => tt.value === v);
              if (t) setEditForm(prev => ({ ...prev, activityDescription: t.label, category: t.category }));
            }} clearable size="sm" />
            <TextInput label="Site / Notes" value={editForm.notes}
              onChange={e => setEditForm(prev => ({ ...prev, notes: e.currentTarget.value }))} size="sm" />
            <SimpleGrid cols={3}>
              {accountCodes.length > 0 && (
                <Select label="Account Code" size="sm"
                  data={accountCodes.map(c => ({ value: c.code, label: `${c.code} — ${c.description}` }))}
                  value={editForm.accountCode || null}
                  onChange={v => setEditForm(prev => ({ ...prev, accountCode: v || '' }))} clearable />
              )}
              {programCodes.length > 0 && (
                <Select label="Program Code" size="sm"
                  data={programCodes.map(c => ({ value: c.code, label: `${c.code} — ${c.description}` }))}
                  value={editForm.programCode || null}
                  onChange={v => setEditForm(prev => ({ ...prev, programCode: v || '' }))} clearable />
              )}
              <Select label="Shift Premium" size="sm" data={PREMIUM_OPTIONS}
                value={editForm.shiftPremium || 'none'}
                onChange={v => setEditForm(prev => ({ ...prev, shiftPremium: v || 'none' }))} />
            </SimpleGrid>
            <Group justify="flex-end">
              <Button variant="light" color="gray" onClick={() => setEditModalOpen(false)}>Cancel</Button>
              <Button color={TEAL} onClick={handleEditSave} loading={editSaving}>Save Changes</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal opened={!!detailRecord} onClose={() => setDetailRecord(null)} title="Time Entry Detail" size="lg">
        {detailRecord && (() => {
          const parsed = parseTaskFromActivity(detailRecord.activityDescription);
          const profile = profileByName.get(detailRecord.employeeName);
          const flags = entryFlags.get(detailRecord.Rid) || [];
          const detailStatus = detailRecord.timesheetOnFile || 'draft';
          const detailIsDraft = !detailRecord.timesheetOnFile || detailRecord.timesheetOnFile === 'draft' || detailRecord.timesheetOnFile === 'no';
          const detailIsRejected = detailRecord.timesheetOnFile === 'rejected';
          const rejectionMatch = (detailRecord.notes || '').match(/^\[REJECTED:\s*(.*?)\]\s*/);
          const rejectionReason = rejectionMatch ? rejectionMatch[1] : '';
          const cleanNotes = (detailRecord.notes || '').replace(/^\[REJECTED:.*?\]\s*/, '');
          return (
            <Stack gap="md">
              {/* Status */}
              <Group justify="space-between">
                <StatusBadge status={detailStatus === 'no' ? 'draft' : detailStatus} />
              </Group>

              {/* Rejection notes */}
              {detailIsRejected && rejectionReason && (
                <Paper p="sm" withBorder style={{ borderLeft: '3px solid #DC2626' }} bg="red.0">
                  <Text size="xs" fw={600} c="red.8" mb={4}>Rejection Reason</Text>
                  <Text size="sm">{rejectionReason}</Text>
                </Paper>
              )}

              {/* Employee & Date */}
              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed">Employee</Text>
                  <Text fw={600}>{detailRecord.employeeName}</Text>
                  <Text size="sm" c="dimmed">{detailRecord.employeeTitle}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Date</Text>
                  <Text fw={600}>{detailRecord.workDate}</Text>
                </div>
              </SimpleGrid>

              {/* Activity & Site */}
              <SimpleGrid cols={2}>
                <div>
                  <Text size="xs" c="dimmed">Activity</Text>
                  <Group gap={6}>
                    <Text fw={500}>{parsed.taskLabel}</Text>
                    {(detailRecord.category || parsed.category) && (
                      <Badge size="xs" variant="light" color="gray">Cat {detailRecord.category || parsed.category}</Badge>
                    )}
                  </Group>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Site / Location</Text>
                  <Text>{cleanNotes || '—'}</Text>
                </div>
              </SimpleGrid>

              {/* Codes & Premium */}
              <Paper p="sm" withBorder>
                <SimpleGrid cols={3}>
                  <div>
                    <Text size="xs" c="dimmed">Account Code</Text>
                    <Text fw={500}>{detailRecord.accountCode || '—'}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Program Code</Text>
                    <Text fw={500}>{detailRecord.programCode || '—'}</Text>
                  </div>
                    <div>
                      <Text size="xs" c="dimmed">Shift Premium</Text>
                      {detailRecord.shiftPremium && detailRecord.shiftPremium !== 'none' ? (
                        <Group gap={6}>
                          <Badge size="sm" variant="light" color="violet">
                            {getPremiumLabel(detailRecord.shiftPremium)}
                          </Badge>
                          <Text size="xs" c="dimmed">({getPremiumMultiplier(detailRecord.shiftPremium)}x)</Text>
                        </Group>
                      ) : (
                        <Text fw={500}>None</Text>
                      )}
                    </div>
                  </SimpleGrid>
                </Paper>

              {/* Hours */}
              <Paper p="sm" withBorder>
                <SimpleGrid cols={4}>
                  <div style={{ textAlign: 'center' }}>
                    <Text size="xs" c="dimmed">Regular Hours</Text>
                    <Text size="lg" fw={700}>{pf(detailRecord.hoursRegular).toFixed(1)}</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text size="xs" c="dimmed">Overtime Hours</Text>
                    <Text size="lg" fw={700}>{pf(detailRecord.hoursOvertime) > 0 ? pf(detailRecord.hoursOvertime).toFixed(1) : '—'}</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text size="xs" c="dimmed">Total Hours</Text>
                    <Text size="lg" fw={700}>{(pf(detailRecord.hoursRegular) + pf(detailRecord.hoursOvertime)).toFixed(1)}</Text>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text size="xs" c="dimmed">Total Cost</Text>
                    <Text size="lg" fw={700} c={TEAL}>{formatCurrency(detailRecord.totalCost)}</Text>
                  </div>
                </SimpleGrid>
              </Paper>

              {/* Rates & Fringe */}
              <Paper p="sm" withBorder>
                <Text size="xs" fw={600} mb="xs" c="dimmed" tt="uppercase">Pay & Fringe Breakdown</Text>
                <SimpleGrid cols={3}>
                  <div>
                    <Text size="xs" c="dimmed">Regular Rate</Text>
                    <Text fw={500}>{formatCurrency(detailRecord.rateRegular)}/hr</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Overtime Rate</Text>
                    <Text fw={500}>{pf(detailRecord.rateOvertime) > 0 ? formatCurrency(detailRecord.rateOvertime) + '/hr' : 'N/A (Exempt)'}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Fringe Rate</Text>
                    <Text fw={500}>{detailRecord.fringeBenefitsPct}%</Text>
                  </div>
                </SimpleGrid>
                <Divider my="xs" />
                <SimpleGrid cols={3}>
                  <div>
                    <Text size="xs" c="dimmed">Labor Cost</Text>
                    <Text fw={500}>{formatCurrency(detailRecord.laborCost)}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Fringe Cost</Text>
                    <Text fw={500}>{formatCurrency(detailRecord.fringeCost)}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Total Cost</Text>
                    <Text fw={700} c={TEAL}>{formatCurrency(detailRecord.totalCost)}</Text>
                  </div>
                </SimpleGrid>
              </Paper>

              {/* Employee Classification (from profile) */}
              {profile && (
                <Paper p="sm" withBorder>
                  <Text size="xs" fw={600} mb="xs" c="dimmed" tt="uppercase">Employee Classification</Text>
                  <SimpleGrid cols={3}>
                    <div>
                      <Text size="xs" c="dimmed">Employment Type</Text>
                      <Badge size="sm" variant="light">{profile.employmentType?.replace(/_/g, ' ') || '—'}</Badge>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">FLSA Status</Text>
                      <Badge size="sm" variant="light" color={profile.flsaStatus === 'exempt' ? 'orange' : 'blue'}>
                        {profile.flsaStatus === 'exempt' ? 'Exempt' : 'Non-Exempt'}
                      </Badge>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Budget Status</Text>
                      <Badge size="sm" variant="light" color={profile.budgetStatus === 'budgeted' ? 'green' : 'gray'}>
                        {profile.budgetStatus === 'budgeted' ? 'Budgeted' : 'Unbudgeted'}
                      </Badge>
                    </div>
                  </SimpleGrid>
                </Paper>
              )}

              {/* Approved Hours (if partial approval) */}
              {detailRecord.timesheetOnFile === 'approved' && detailRecord.approvedRegHours && (() => {
                const loggedReg = pf(detailRecord.hoursRegular);
                const loggedOT = pf(detailRecord.hoursOvertime);
                const approvedReg = pf(detailRecord.approvedRegHours);
                const approvedOT = pf(detailRecord.approvedOTHours);
                const isPartial = approvedReg !== loggedReg || approvedOT !== loggedOT;
                if (!isPartial) return null;
                return (
                  <Paper p="sm" withBorder style={{ borderLeft: '3px solid #F59E0B' }} bg="orange.0">
                    <Text size="xs" fw={600} mb="xs" c="orange.8">Hours Adjusted by Supervisor</Text>
                    <SimpleGrid cols={2}>
                      <div>
                        <Text size="xs" c="dimmed">Logged</Text>
                        <Text fw={500}>{loggedReg.toFixed(1)} reg + {loggedOT.toFixed(1)} OT = {(loggedReg + loggedOT).toFixed(1)} hrs</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Approved</Text>
                        <Text fw={700} c="orange.8">
                          {approvedReg.toFixed(1)} reg + {approvedOT.toFixed(1)} OT = {(approvedReg + approvedOT).toFixed(1)} hrs
                          {approvedReg === 0 && approvedOT > 0 && ' (OT only)'}
                          {approvedReg > 0 && approvedOT === 0 && loggedOT > 0 && ' (Reg only)'}
                        </Text>
                      </div>
                    </SimpleGrid>
                    <SimpleGrid cols={2} mt="xs">
                      <div>
                        <Text size="xs" c="dimmed">Original Cost</Text>
                        <Text fw={500}>{formatCurrency(detailRecord.totalCost)}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Approved Cost</Text>
                        <Text fw={700} c="orange.8">{formatCurrency(detailRecord.approvedCost)}</Text>
                      </div>
                    </SimpleGrid>
                    {detailRecord.reviewerNotes && (
                      <>
                        <Divider my="xs" />
                        <Text size="xs" c="dimmed">Reviewer Notes</Text>
                        <Text size="sm">{detailRecord.reviewerNotes}</Text>
                      </>
                    )}
                  </Paper>
                );
              })()}

              {/* Project */}
              {detailRecord.projectId && (
                <div>
                  <Text size="xs" c="dimmed">Assigned Project</Text>
                  <Text>{projectLookup.get(detailRecord.projectId) || detailRecord.projectId}</Text>
                </div>
              )}

              {/* Eligibility Flags */}
              {flags.length > 0 && (
                <Paper p="sm" withBorder style={{ borderLeft: '3px solid #F59E0B' }}>
                  <Text size="xs" fw={600} mb="xs" c="orange.8">PAPPG Eligibility Notes</Text>
                  {flags.map((f, i) => (
                    <Group key={i} gap="xs" mb={4}>
                      <IconAlertTriangle size={14} color="#F59E0B" />
                      <Text size="sm">{f.message}</Text>
                      <Text size="xs" c="dimmed">({f.rule})</Text>
                    </Group>
                  ))}
                </Paper>
              )}

              {/* Actions */}
              <Group justify="flex-end" gap="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-2)', paddingTop: 12 }}>
                <Button variant="light" color="gray" onClick={() => setDetailRecord(null)}>Close</Button>
                {canEdit && detailIsDraft && (
                  <Button color="blue" leftSection={<IconSend size={14} />}
                    onClick={() => handleSubmitForReview(detailRecord.Rid)}>
                    Submit for Review
                  </Button>
                )}
                {canEdit && detailIsRejected && (
                  <Button color="blue" leftSection={<IconSend size={14} />}
                    onClick={() => handleResubmit(detailRecord.Rid)}>
                    Resubmit for Review
                  </Button>
                )}
                {canEdit && (
                  <>
                    <Button variant="light" color="blue" leftSection={<IconEdit size={14} />}
                      onClick={() => { setDetailRecord(null); openEdit(detailRecord); }}>Edit</Button>
                    <Button variant="light" color="red" leftSection={<IconTrash size={14} />}
                      onClick={() => { setDetailRecord(null); setDeleteTarget(detailRecord); }}>Delete</Button>
                  </>
                )}
              </Group>
            </Stack>
          );
        })()}
      </Modal>

      {/* Delete Confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Time Entry" size="sm">
        <Text size="sm" mb="md">
          Delete time entry for <strong>{deleteTarget?.employeeName}</strong> on {deleteTarget?.workDate}?
        </Text>
        <Group justify="flex-end">
          <Button variant="light" color="gray" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="red" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>

    </div>
  );
}
